/**
 * FeaturedProducts Component
 * Showcase best-selling or featured products
 */

import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import type { Product } from '~/lib/shopify/types';
import { getProducts } from '~/lib/shopify/api';
import { useServerData } from '~/lib/server-data';
import { ProductCard } from '../product/ProductCard';

// Mock products for demo
const mockProducts: Product[] = [
  {
    id: '1',
    handle: 'jabra-evolve2-85',
    title: 'Jabra Evolve2 85 MS Stereo Wireless Bluetooth Headset',
    description: 'Premium wireless headset with ANC',
    descriptionHtml: '<p>Premium wireless headset with ANC</p>',
    vendor: 'Jabra',
    productType: 'Headset',
    tags: ['new', 'wireless', 'anc'],
    availableForSale: true,
    totalInventory: 50,
    seo: { title: null, description: null },
    featuredImage: null,
    images: { edges: [] },
    options: [{ id: '1', name: 'USB Type', values: ['USB-A', 'USB-C'] }],
    variants: { edges: [{ node: { id: 'v1', title: 'USB-A', availableForSale: true, quantityAvailable: 25, sku: 'JBR-E285-A', price: { amount: '299.99', currencyCode: 'GBP' }, compareAtPrice: { amount: '349.99', currencyCode: 'GBP' }, selectedOptions: [{ name: 'USB Type', value: 'USB-A' }], image: null } }] },
    priceRange: { minVariantPrice: { amount: '299.99', currencyCode: 'GBP' }, maxVariantPrice: { amount: '299.99', currencyCode: 'GBP' } },
    compareAtPriceRange: { minVariantPrice: { amount: '349.99', currencyCode: 'GBP' }, maxVariantPrice: { amount: '349.99', currencyCode: 'GBP' } },
    metafields: [],
  },
  {
    id: '2',
    handle: 'poly-voyager-focus-2',
    title: 'Poly Voyager Focus 2 UC Wireless Bluetooth Headset',
    description: 'Active Noise Cancelling wireless headset',
    descriptionHtml: '<p>Active Noise Cancelling wireless headset</p>',
    vendor: 'Poly',
    productType: 'Headset',
    tags: ['wireless', 'anc', 'teams'],
    availableForSale: true,
    totalInventory: 30,
    seo: { title: null, description: null },
    featuredImage: null,
    images: { edges: [] },
    options: [{ id: '1', name: 'USB Type', values: ['USB-A', 'USB-C'] }],
    variants: { edges: [{ node: { id: 'v2', title: 'USB-A', availableForSale: true, quantityAvailable: 15, sku: 'PLY-VF2-A', price: { amount: '249.99', currencyCode: 'GBP' }, compareAtPrice: null, selectedOptions: [{ name: 'USB Type', value: 'USB-A' }], image: null } }] },
    priceRange: { minVariantPrice: { amount: '249.99', currencyCode: 'GBP' }, maxVariantPrice: { amount: '249.99', currencyCode: 'GBP' } },
    compareAtPriceRange: { minVariantPrice: { amount: '249.99', currencyCode: 'GBP' }, maxVariantPrice: { amount: '249.99', currencyCode: 'GBP' } },
    metafields: [],
  },
  {
    id: '3',
    handle: 'axtel-voice-28-hd',
    title: 'Axtel Voice 28 HD Mono USB Wired Headset',
    description: 'Professional call centre headset',
    descriptionHtml: '<p>Professional call centre headset</p>',
    vendor: 'Axtel',
    productType: 'Headset',
    tags: ['wired', 'call-centre'],
    availableForSale: true,
    totalInventory: 100,
    seo: { title: null, description: null },
    featuredImage: null,
    images: { edges: [] },
    options: [],
    variants: { edges: [{ node: { id: 'v3', title: 'Default', availableForSale: true, quantityAvailable: 100, sku: 'AXL-V28-HD', price: { amount: '49.99', currencyCode: 'GBP' }, compareAtPrice: null, selectedOptions: [], image: null } }] },
    priceRange: { minVariantPrice: { amount: '49.99', currencyCode: 'GBP' }, maxVariantPrice: { amount: '49.99', currencyCode: 'GBP' } },
    compareAtPriceRange: { minVariantPrice: { amount: '49.99', currencyCode: 'GBP' }, maxVariantPrice: { amount: '49.99', currencyCode: 'GBP' } },
    metafields: [],
  },
  {
    id: '4',
    handle: 'jabra-engage-55',
    title: 'Jabra Engage 55 UC Stereo DECT Wireless Headset',
    description: 'Enterprise-grade DECT wireless headset',
    descriptionHtml: '<p>Enterprise-grade DECT wireless headset</p>',
    vendor: 'Jabra',
    productType: 'Headset',
    tags: ['wireless', 'dect', 'enterprise'],
    availableForSale: true,
    totalInventory: 20,
    seo: { title: null, description: null },
    featuredImage: null,
    images: { edges: [] },
    options: [],
    variants: { edges: [{ node: { id: 'v4', title: 'Default', availableForSale: true, quantityAvailable: 5, sku: 'JBR-ENG55', price: { amount: '379.99', currencyCode: 'GBP' }, compareAtPrice: { amount: '429.99', currencyCode: 'GBP' }, selectedOptions: [], image: null } }] },
    priceRange: { minVariantPrice: { amount: '379.99', currencyCode: 'GBP' }, maxVariantPrice: { amount: '379.99', currencyCode: 'GBP' } },
    compareAtPriceRange: { minVariantPrice: { amount: '429.99', currencyCode: 'GBP' }, maxVariantPrice: { amount: '429.99', currencyCode: 'GBP' } },
    metafields: [],
  },
];

export function FeaturedProducts() {
  const serverData = useServerData();
  const initialProducts = serverData.products?.slice(0, 8);
  const [products, setProducts] = useState<Product[]>(initialProducts || []);
  const [isLoading, setIsLoading] = useState(!initialProducts);

  useEffect(() => {
    if (initialProducts && initialProducts.length > 0) return;

    async function fetchProducts() {
      try {
        const { products } = await getProducts({ first: 8, sortKey: 'BEST_SELLING' });
        setProducts(products);
      } catch (error) {
        console.error('Failed to fetch products, using mock data:', error);
        setProducts(mockProducts);
      } finally {
        setIsLoading(false);
      }
    }

    fetchProducts();
  }, []);

  return (
    <section className="py-20 lg:py-32">
      <div className="container-wide">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12"
        >
          <div>
            <span className="text-2xs font-mono uppercase tracking-widest text-brand-orange mb-4 block">
              Top Picks
            </span>
            <h2 className="heading-lg text-accent-white">
              Best Sellers
            </h2>
          </div>

          <a
            href="/products"
            className="btn-secondary self-start sm:self-auto"
          >
            View All
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </a>
        </motion.div>

        {/* Products Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="card overflow-hidden">
                <div className="aspect-square bg-surface-graphite skeleton" />
                <div className="p-4 space-y-3">
                  <div className="h-4 bg-surface-slate skeleton rounded w-3/4" />
                  <div className="h-4 bg-surface-slate skeleton rounded w-1/2" />
                  <div className="h-6 bg-surface-slate skeleton rounded w-24" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.slice(0, 4).map((product, index) => (
              <ProductCard
                key={product.id}
                product={product}
                priority={index < 2}
                index={index}
              />
            ))}
          </div>
        )}

        {/* Secondary Row */}
        {!isLoading && products.length > 4 && (
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {products.slice(4, 8).map((product, index) => (
              <ProductCard
                key={product.id}
                product={product}
                index={index + 4}
              />
            ))}
          </motion.div>
        )}
      </div>
    </section>
  );
}
